// @ts-nocheck - TODO: properly type this file!

import React, { useEffect, useState } from 'react';
import { Select, Tooltip } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import { CustomButton, CustomTitle } from '../../../components';
import ShipmentPreview from './ShipmentPreview';
import ShipmentPreviewCard from './ShipmentPreviewCard';
import './StepOneMapping.css';
import tabLeft from '../../../assets/images/tabLeft.svg';
import tabRight from '../../../assets/images/tabRight.svg';
import theme from '@/assets/theme';
import { themeT } from '@/utils/themeText';

const { Option } = Select;

interface FieldMapping {
  header_name: string;
  actual_name?: string;
  enable?: boolean;
  value?: string;
}

interface StepOneMappingProps {
  onCancel: () => void;
  onNext: () => void;
  fieldMappings?: FieldMapping[];
  mapping: Record<number, string>;
  handleToggle: (index: number, checked: boolean) => void;
  handleMappingChange: (index: number, value: string) => void;
  setMapping: (mapping: Record<number, string>) => void;
}

const SENSOR_SLOT_COUNT = 24;

const buildZoomLogiFieldMap = (): Record<string, string> => {
  const base: Record<string, string> = {
    reference_number: themeT('referenceId', theme.themeType),
    courier: themeT('transportProvider', theme.themeType),
    courier_tracking_id: `${themeT('transportProvider', theme.themeType)} Tracking ID`,
    sensor: 'Sensor provider',
    sensor_tracking_id: 'Sensor ID',
    products: 'Products',
    teams: `${themeT('team', theme.themeType)}s`,
    from_location_name: 'Origin name',
    from_location_address: 'Origin address',
    to_location_name: 'Destination name',
    to_location_address: 'Destination address',
    link_code: themeT('linkCode', theme.themeType),
  };
  for (let i = 1; i <= SENSOR_SLOT_COUNT; i += 1) {
    base[`sensor_slot_${i}_name`] = `Sensor provider ${i}`;
    base[`sensor_slot_${i}_tracking_id`] = `Sensor ID ${i}`;
  }
  return base;
};

const ZoomLogiFieldMap = buildZoomLogiFieldMap();
const dummyCard = {
  id: 'SH123456',
  reference_number: 'REF-987654',
  status: 'IN TRANSIT',
  type: 1,
  from_location: {
    name: 'Colorado Warehouse',
    parsed_address: { PlaceName: 'Denver', StateName: 'CO', ZipCode: '80202' },
  },
  to_location: {
    name: 'General Hospital',
    parsed_address: { PlaceName: 'Austin', StateName: 'TX', ZipCode: '73301' },
  },
  scheduled_pickup_date: '2024-08-10',
  scheduled_delivery_date: '2024-08-13',
  courier: { title: 'cold chain express' },
  teams: [
    { name: 'Team Alpha', color: 'var(--light-blue)' },
    { name: 'Team Beta', color: '#FF9800' },
  ],
  legs_info: {
    legs: [
      { status: 'travelled', mode: 'truck', distribution: 50 },
      { status: 'pending', mode: 'sky', distribution: 50 },
    ],
  },
};

/**
 * Step one mapping component for bulk upload field mapping
 *
 * @param onCancel - Function to cancel the mapping process
 * @param onNext - Function to proceed to next step
 * @param fieldMappings - Array of field mappings from uploaded file
 * @param mapping - Current field mapping state
 * @param handleToggle - Function to toggle field enable/disable
 * @param handleMappingChange - Function to handle mapping value changes
 * @param setMapping - Function to set the mapping state
 */
const StepOneMapping: React.FC<StepOneMappingProps> = ({
  onCancel,
  onNext,
  fieldMappings,
  mapping,
  handleToggle,
  handleMappingChange,
  setMapping,
}) => {
  const [previewVisible, setPreviewVisible] = useState(false);

  useEffect(() => {
    const initialMapping: Record<number, string> = {};
    fieldMappings?.forEach((f: FieldMapping, index: number) => {
      if (
        f.enable !== false &&
        f.actual_name &&
        ZoomLogiFieldMap[f.actual_name]
      ) {
        initialMapping[index] = f.actual_name;
      }
    });
    setMapping(initialMapping);
  }, [fieldMappings, setMapping]);

  const previewData = {
    reference_number: '',
    courier: null,
    sensor: null,
    courier_tracking_id: '',
    sensor_tracking_id: '',
  };

  fieldMappings?.forEach((field: FieldMapping, index: number) => {
    const mappedTo = mapping[index];
    const isEnabled = field.enable !== false;

    if (isEnabled && ZoomLogiFieldMap[mappedTo]) {
      switch (mappedTo) {
        case 'reference_number':
        case 'courier_tracking_id':
        case 'sensor_tracking_id':
          previewData[mappedTo] = field.header_name;
          break;
        case 'courier':
        case 'sensor':
          previewData[mappedTo] = { title: field.header_name };
          break;
        default:
          break;
      }
    }
  });

  const dynamicCard = {
    ...dummyCard,
    ...previewData,
  };

  const valueMap: Record<string, string> = {};
  fieldMappings?.forEach((item: FieldMapping, index: number) => {
    if (item.actual_name && item.enable) {
      valueMap[item.actual_name + '_' + index] = item.value;
    }
  });

  const getValue = (key: string): string => {
    const match = fieldMappings?.find(
      (f: FieldMapping) => f.actual_name === key && f.enable !== false
    );
    return match?.value || '--';
  };

  const shipment = {
    reference_number: getValue('reference_number'),
    courier: { title: getValue('courier') },
    sensor: {
      type: getValue('sensor'),
      id: getValue('sensor_tracking_id'),
    },
    courier_tracking_id: getValue('courier_tracking_id'),
    from_location: {
      name: getValue('from_location_name'),
      parsed_address: { PlaceName: getValue('from_location_address') },
    },
    to_location: {
      name: getValue('to_location_name'),
      parsed_address: { PlaceName: getValue('to_location_address') },
    },
    product: {
      name: getValue('products'),
      product_type: '--',
    },
    contact_phone: '--',
    scheduled_pickup_date: getValue('scheduled_pickup_date'),
    scheduled_delivery_date: getValue('scheduled_delivery_date'),
    estimated_delivery_date: '--',
    delivery_status: '--',
    monitor_info: {
      temperature: '--',
      humidity: '--',
      battery: '--',
    },
    teams: [{ name: 'Team Alpha', color: 'var(--light-blue)' }],
    volume: getValue('volume'),
    weight_kg: getValue('weight_kg'),
    transport_provider_eta: '--',
  };
  const isEnabled = (fieldName: string): boolean =>
    fieldMappings?.some(
      (f: FieldMapping) => f.actual_name === fieldName && f.enable !== false
    ) || false;

  const hasNumberedSensorSlotField =
    fieldMappings?.some(
      (f: FieldMapping) =>
        f.enable !== false &&
        f.actual_name &&
        /^sensor_slot_\d+_(name|tracking_id)$/.test(f.actual_name)
    ) ?? false;

  const missing: string[] = [];
  /** Shown next to Zoomlogi field names in the mapping dropdown (pair is validated together). */
  const requiredFields = ['courier', 'courier_tracking_id'];

  const sensorEnabled = isEnabled('sensor');
  const courierEnabled = isEnabled('courier');

  if (!sensorEnabled && !courierEnabled && !hasNumberedSensorSlotField) {
    missing.push(`Sensor or ${themeT('transportProvider', theme.themeType)}`);
  }

  if (sensorEnabled && !isEnabled('sensor_tracking_id')) {
    missing.push('Sensor ID (required when Sensor is enabled)');
  }

  if (courierEnabled && !isEnabled('courier_tracking_id')) {
    missing.push(
      `${themeT('transportProvider', theme.themeType)} Tracking ID (required when ${themeT('transportProvider', theme.themeType)} is enabled)`
    );
  }

  const isFormValid = missing.length === 0;

  const tooltipMessage = !isFormValid ? `Missing: ${missing.join(', ')}` : '';

  return (
    <div className='step-one-mapping-root'>
      <CustomTitle
        text='Bulk upload'
        fontSize='20px'
        lineHeight='28px'
        fontWeight='700'
        marginBottom='4px'
      />
      <p
        style={{
          fontSize: '14px',
          color: 'var(--text-secondary)',
          marginTop: '10px',
          marginBottom: 0,
          paddingBottom: '14px',
          flexShrink: 0,
        }}
      >
        Review and match each column to the correct data field to ensure
        accurate importing
      </p>

      <div className='step-one-mapping'>
        <div className={`step-one-left ${!previewVisible ? 'full-width' : ''}`}>
          <div className='step-one-header'>
            <div
              style={{
                width: '37%',
                marginLeft: !previewVisible ? '4%' : '7%',
              }}
            >
              <p
                style={{
                  fontWeight: 600,
                  fontSize: 12,
                  color: 'var(--text-secondary)',
                  marginBottom: 4,
                }}
              >
                From table.xlsx
              </p>
            </div>
            <div style={{ width: '8.5%', textAlign: 'center' }}></div>
            <div style={{ width: '45%' }}>
              <p
                style={{
                  fontWeight: 600,
                  fontSize: 12,
                  color: 'var(--text-secondary)',
                  marginBottom: 4,
                  marginLeft: !previewVisible ? '6%' : '0%',
                }}
              >
                Zoomlogi data field (<span style={{ color: 'red' }}>*</span> =
                required)
              </p>
            </div>
          </div>

          {fieldMappings?.map((field: FieldMapping, index: number) => (
            <div className='step-one-row' key={index}>
              <div className='step-one-col'>
                <div className='cl-toggle-switch'>
                  <label className='cl-switch'>
                    <input
                      style={{ height: 36 }}
                      type='checkbox'
                      checked={field.enable ?? true}
                      onChange={(e) => handleToggle(index, e.target.checked)}
                    />
                    <span className='cl-slider'></span>
                  </label>
                </div>
                <input
                  disabled={!field.enable}
                  value={field.header_name}
                  readOnly
                  style={{
                    flex: 1,
                    height: 36,
                    padding: '6px 10px',
                    borderRadius: 6,
                    border: !field.enable ? 'none' : '1px solid #ccc',
                    backgroundColor: !field.enable ? '#f5f5f5' : '#fff',
                    color: !field.enable
                      ? 'rgba(0, 0, 0, 0.4'
                      : 'rgba(0, 0, 0, 0.8)',
                  }}
                />
              </div>

              {field.enable !== false && (
                <>
                  <div className='step-one-arrow'>
                    <ArrowRightOutlined style={{ fontSize: 16 }} />
                  </div>
                  <div className='step-one-select'>
                    <Select
                      style={{
                        height: 36,
                        width: '100%',
                        backgroundColor: 'var(--light-gray)',
                        borderRadius: 6,
                      }}
                      placeholder='Select field'
                      value={mapping[index] || undefined}
                      onChange={(val) => handleMappingChange(index, val)}
                    >
                      {Object.entries(ZoomLogiFieldMap).map(
                        ([value, label]) => (
                          <Option key={value} value={value}>
                            {label}
                            {requiredFields.includes(value) ? (
                              <span style={{ color: 'red' }}> *</span>
                            ) : null}
                          </Option>
                        )
                      )}
                    </Select>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {previewVisible && (
          <div className='shipment-preview'>
            <div
              style={{
                fontWeight: 600,
                fontSize: 16,
                marginBottom: 16,
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              Shipment preview
            </div>
            <div style={{ fontSize: 13, marginBottom: 16 }}>
              <ShipmentPreviewCard
                shipment={shipment}
                card={dynamicCard}
                toggleDrawer={() => {}}
              />
            </div>
            <div
              style={{
                maxHeight: '520px',
                overflowY: 'auto',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              }}
              className='no-scrollbar'
            >
              <ShipmentPreview shipment={shipment} />
            </div>
          </div>
        )}

        <button
          onClick={() => setPreviewVisible((prev) => !prev)}
          className='toggle-preview-btn'
          type='button'
        >
          {previewVisible ? (
            <Tooltip title='Collapse preview'>
              <img src={tabRight} alt='Collapse preview' />
            </Tooltip>
          ) : (
            <Tooltip title='Expand preview'>
              <img src={tabLeft} alt='Expand preview' />
            </Tooltip>
          )}
        </button>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: 24,
          gap: 14,
          flexWrap: 'wrap',
          flexShrink: 0,
        }}
      >
        <CustomButton
          key='cancel'
          width={'80px'}
          onClick={onCancel}
          text={'Cancel'}
          bgColor='transparent'
          color='black'
          fontSize='14px'
          fontWeight='600'
          boxShadow='none'
          height='36px'
        />
        <Tooltip title={tooltipMessage}>
          <div style={{ display: 'inline-block' }}>
            <CustomButton
              key='next'
              text='Next'
              width={'80px'}
              height='36px'
              borderRadius='8px'
              bgColor='#000'
              color='#fff'
              border='0px'
              fontSize='14px'
              fontWeight='500'
              isFormValid={!isFormValid}
              onClick={onNext}
            />
          </div>
        </Tooltip>
      </div>
    </div>
  );
};

export default StepOneMapping;
